import { Controller } from "@hotwired/stimulus"
import { subscribeToCandles } from "../channels/candles_subscription"

const PAGE_SIZE = 100

export default class extends Controller {
  static targets = ["tbody", "timeframeButton", "sentinel", "status"]
  static values = {
    pairId: Number,
    timeframe: { type: String, default: "1h" }
  }

  activate() {
    if (this.started) return
    this.started = true
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) this.loadOlder()
    })
    this.observer.observe(this.sentinelTarget)
    this.reload()
    this.subscribe()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  subscribe() {
    this.subscription = subscribeToCandles({
      pairId: this.pairIdValue,
      timeframe: this.timeframeValue,
      onUpdate: (data) => this.applyLiveCandles(data.candles)
    })
  }

  resubscribe() {
    if (this.subscription) this.subscription.unsubscribe()
    this.subscribe()
  }

  switchTimeframe(event) {
    const timeframe = event.currentTarget.dataset.timeframe
    if (timeframe === this.timeframeValue) return
    this.timeframeValue = timeframe
    this.timeframeButtonTargets.forEach((btn) => {
      btn.classList.toggle("tf-active", btn.dataset.timeframe === timeframe)
    })
    this.reload()
    this.resubscribe()
  }

  reload() {
    this.tbodyTarget.innerHTML = ""
    this.cursor = null
    this.hasMore = true
    this.newestTime = null
    this.loadOlder()
  }

  async loadOlder() {
    if (!this.hasMore || this.loading) return
    this.loading = true
    this.statusTarget.textContent = "loading…"

    try {
      const data = await this.fetchCandles()
      // endpoint returns ascending; the table is newest-first → reverse to descending
      const descending = data.candles.slice().reverse()
      descending.forEach((candle) => this.tbodyTarget.appendChild(this.buildRow(candle)))
      if (this.newestTime === null && descending.length) {
        this.newestTime = descending[0].time
      }
      this.cursor = data.oldest_open_time
      this.hasMore = data.has_more
      this.statusTarget.textContent = this.hasMore ? "" : "— start of history —"
    } catch (error) {
      this.statusTarget.textContent = "— failed to load · scroll to retry —"
    } finally {
      this.loading = false
    }
  }

  // Applies pushed candles: a newer candle prepends a row, the same-time candle
  // replaces the top row, an older candle is ignored.
  applyLiveCandles(candles) {
    candles.forEach((c) => {
      if (this.newestTime !== null && c.time < this.newestTime) return

      if (this.newestTime !== null && c.time === this.newestTime) {
        const top = this.tbodyTarget.firstElementChild
        if (top) top.replaceWith(this.buildRow(c))
      } else {
        this.tbodyTarget.prepend(this.buildRow(c))
        this.newestTime = c.time
      }
    })
  }

  async fetchCandles() {
    const url = new URL(
      `/trading_pairs/${this.pairIdValue}/candles.json`,
      window.location.origin
    )
    url.searchParams.set("timeframe", this.timeframeValue)
    url.searchParams.set("limit", String(PAGE_SIZE))
    if (this.cursor) url.searchParams.set("before", this.cursor)

    const response = await fetch(url)
    if (!response.ok) throw new Error(`candles request failed: ${response.status}`)
    return response.json()
  }

  buildRow(candle) {
    const tr = document.createElement("tr")
    tr.className =
      "border-t border-[var(--color-line-soft)] hover:bg-[var(--color-elev-2)]"
    const openTime = new Date(candle.time * 1000)
      .toISOString()
      .replace("T", " ")
      .slice(0, 16)
    const closeClass =
      candle.close >= candle.open
        ? "text-[var(--color-accent)]"
        : "text-[var(--color-danger)]"

    tr.innerHTML = `
      <td class="px-4 py-2 text-[var(--color-fg-muted)]">${this.timeframeValue}</td>
      <td class="px-4 py-2 text-[var(--color-fg-muted)] text-xs">${openTime}</td>
      <td class="px-4 py-2 text-right">${candle.open}</td>
      <td class="px-4 py-2 text-right">${candle.high}</td>
      <td class="px-4 py-2 text-right">${candle.low}</td>
      <td class="px-4 py-2 text-right ${closeClass}">${candle.close}</td>
      <td class="px-4 py-2 text-right text-[var(--color-fg-muted)]">${candle.volume}</td>
    `
    return tr
  }
}
